class SunbowTheme {
  constructor() {
    this.bindQuoteForms();
    this.bindSalesInquiryForms();
    this.bindQuoteModal();
    this.bindGalleries();
    this.bindStyleCards();
    this.bindStyleGalleries();
    this.captureAttribution();
  }

  bindQuoteForms() {
    document.querySelectorAll('.quote-form').forEach((form) => {
      const expand = () => form.classList.add('expanded');
      const collapse = () => form.classList.remove('expanded');

      form.addEventListener('focusin', expand);
      form.addEventListener('pointerdown', expand);
      // Mobile keyboards, autofill, and native controls can blur a field while
      // the customer is still working. Only an intentional outside click should
      // collapse the form.
      document.addEventListener('click', (event) => {
        if (form.closest('[data-quote-modal]:not([hidden])')) return;
        if (!form.contains(event.target)) collapse();
      });
    });
  }

  bindSalesInquiryForms() {
    const formTypes = { 'Greenhouse quote': 'greenhouse_quote', 'General contact': 'general_contact' };
    const readState = key => { try { return JSON.parse(window.sessionStorage.getItem(key) || 'null'); } catch { return null; } };
    const saveState = (key,state) => { try { window.sessionStorage.setItem(key,JSON.stringify(state)); } catch {} };
    const published = new Set();
    const publishAccepted = (lead) => {
      const id=lead.submission_id; const marker=`sunbow:inquiry-published:${id}`;
      const wasPublished = () => { try { return window.localStorage.getItem(marker)==='1'; } catch { return false; } };
      if (published.has(id) || wasPublished()) return;
      published.add(id);
      let attempts=0;
      const attempt = async () => {
        if (wasPublished()) return;
        attempts++;
        try {
          if (!window.Shopify?.analytics?.publish) throw new Error('Publisher loading');
          const accepted=await window.Shopify.analytics.publish('sunbow:sales_inquiry_submitted',{...lead,page_path:window.location.pathname});
          if (!accepted) throw new Error('Publisher unavailable');
          try { window.localStorage.setItem(marker,'1'); } catch {}
          document.documentElement.dataset.sunbowLeadTracking='completed';
        } catch {
          document.documentElement.dataset.sunbowLeadTracking='waiting-api';
          if (attempts<60) window.setTimeout(attempt,500);
          else published.delete(id);
        }
      };
      void attempt();
    };
    let tokenPromise;
    let tokenReadyAt=0;
    const getToken = () => {
      if (tokenPromise && Date.now()-tokenReadyAt > 3600000) tokenPromise=null;
      if (!tokenPromise) tokenPromise=fetch(`/apps/quote/intake-token?v=${Date.now()}`,{credentials:'same-origin',cache:'no-store'})
        .then(async response => { if (!response.ok) throw new Error('Could not prepare form'); const json=await response.json(); tokenReadyAt=Date.now()+1100; return json.token; })
        .catch(error => { tokenPromise=null; throw error; });
      return tokenPromise;
    };
    document.querySelectorAll('form[data-sunbow-inquiry]').forEach(form => {
      const formType=formTypes[form.querySelector('[name="contact[request_type]"]')?.value];
      if (!formType || form.dataset.inquiryBound==='true') return;
      form.dataset.inquiryBound='true';
      const contextParams=new URLSearchParams(window.location.search||'');
      const contextKeys=['product_family', 'product_title', 'shape', 'width', 'length', 'ventilation', 'metal_end_wall_package', 'two_point_trusses', 'two_point_trusses_price', 'greenhouse_plastic', 'greenhouse_plastic_upgrade_price', 'configured_price', 'product_url', 'variant'];
      const projectContext=form.id==='SunbowContactForm'?Object.fromEntries(contextKeys.map(key=>[key,contextParams.get(key)||'']).filter(([,value])=>value)):{};
      const contextJSON=Object.keys(projectContext).length?JSON.stringify(projectContext):'';
      if(contextJSON){
        const field=document.createElement('input');field.type='hidden';field.name='contact[project_context]';field.value=contextJSON;form.append(field);
        const note=document.createElement('p');note.className='form-note';note.textContent=`Selected kit: ${projectContext.product_title||projectContext.product_family||'Greenhouse'}${projectContext.width&&projectContext.length?' · '+projectContext.width+' × '+projectContext.length:''}`;form.prepend(note);
      }
      const stateKey=`sunbow:inquiry:${window.location.pathname}:${form.id}${contextJSON?':'+contextJSON:''}`;
      const originalHTML=form.innerHTML;
      let state=readState(stateKey);
      if (!state || !state.id) state={id:window.crypto?.randomUUID?.()||`${Date.now()}-${Math.random().toString(16).slice(2)}`,createdAt:Date.now(),timestamp:new Date().toISOString()};
      const prepareFields = () => {
        form.querySelector('[data-submission-id]').value=state.id;
        form.querySelector('[data-submission-timestamp]').value=state.timestamp;
        form.querySelector('[data-form-type]').value=formType;
        form.querySelector('[data-start-quote-url]').value=`https://quotes.sunbowgreenhouses.com/staff?lead=${encodeURIComponent(state.id)}`;
      };
      const showSuccess = lead => {
        form.replaceChildren();
        const heading=document.createElement('div'); heading.className='form-heading sunbow-contact__alert sunbow-contact__alert--success'; heading.tabIndex=-1; heading.setAttribute('role','status');
        const title=document.createElement('h2'); title.textContent='Thank you. We’ll be in touch.';
        const message=document.createElement('p'); message.textContent='Your request has been received by Sunbow Greenhouses.';
        heading.append(title,message); form.append(heading); form.classList.add('expanded');
        const another=document.createElement('button'); another.type='button';another.className='sunbow-button';another.textContent='Send another inquiry';
        another.addEventListener('click',()=>{form.innerHTML=originalHTML;state={id:window.crypto?.randomUUID?.()||`${Date.now()}-${Math.random().toString(16).slice(2)}`,createdAt:Date.now(),timestamp:new Date().toISOString()};saveState(stateKey,state);prepareFields();void getToken().catch(()=>{});});
        form.append(another);heading.focus();publishAccepted(lead);
      };
      prepareFields();saveState(stateKey,state);
      if (state.accepted && state.lead) showSuccess(state.lead);
      else void getToken().catch(()=>{});
      form.addEventListener('submit',async event => {
        event.preventDefault();event.stopImmediatePropagation();
        if (state.accepted || form.dataset.intakeSubmitting==='true' || !form.reportValidity()) return;
        form.dataset.intakeSubmitting='true';
        const submitter=event.submitter||form.querySelector('[type="submit"]');const label=submitter?.innerHTML;
        if(submitter){submitter.disabled=true;submitter.textContent='Sending…';}
        form.querySelector('[data-intake-error]')?.remove();
        try {
          const token=await getToken();
          if (Date.now()<tokenReadyAt) await new Promise(resolve=>window.setTimeout(resolve,tokenReadyAt-Date.now()));
          const attribution=window.SunbowAttribution?.capture?.();window.SunbowAttribution?.populate?.(form,attribution);
          prepareFields();
          const body=new URLSearchParams(new FormData(form));body.set('inquiry_token',token);
          const response=await fetch('/apps/quote/inquiries',{method:'POST',headers:{'content-type':'application/x-www-form-urlencoded;charset=UTF-8','accept':'application/json'},body,credentials:'same-origin',signal:AbortSignal.timeout(25000)});
          if (response.status===422) tokenPromise=null;
          let result;
          try { result=await response.json(); } catch { throw new Error('We could not confirm your request. Please check your connection and retry with the same form.'); }
          if (!response.ok || result.accepted!==true || result.ok!==true || result.submission_id!==state.id || result.lead?.submission_id!==state.id) { const rejection=new Error(response.status===409?'This request was already received with different details. Select Start a new inquiry to send another.':result.message||'We could not confirm your request. Please check your connection and try again.'); rejection.status=response.status; throw rejection; }
          state.accepted=true;state.lead=result.lead;saveState(stateKey,state);
          showSuccess(result.lead);
        } catch(error) {
          if (/reload/i.test(error.message)) tokenPromise=null;
          const message=document.createElement('p');message.dataset.intakeError='';message.setAttribute('role','alert');message.className='form-note';message.textContent=error.name==='TimeoutError'?'We could not confirm your request yet. Please retry; your request ID stays the same.':error.message||'Please check your connection and try again.';
          (form.querySelector('.form-actions')||form).append(message);
          if(error.status===409){const restart=document.createElement('button');restart.type='button';restart.className='sunbow-button';restart.textContent='Start a new inquiry';restart.addEventListener('click',()=>{state={id:window.crypto?.randomUUID?.()||`${Date.now()}-${Math.random().toString(16).slice(2)}`,createdAt:Date.now(),timestamp:new Date().toISOString()};prepareFields();saveState(stateKey,state);message.remove();});message.append(restart);}
          if(submitter){submitter.disabled=false;submitter.innerHTML=label;}
        } finally {form.dataset.intakeSubmitting='false';}
      },true);
    });
  }

  bindQuoteModal() {
    const modal = document.querySelector('[data-quote-modal]');
    const form = document.querySelector('#SunbowQuoteForm');
    const content = modal?.querySelector('[data-quote-modal-content]');
    const closeButton = modal?.querySelector('[data-quote-modal-close]');
    if (!modal || !form || !content || !closeButton) return;

    const formHome = form.parentElement;
    let previousFocus = null;

    const close = () => {
      if (modal.hidden) return;
      form.classList.remove('expanded');
      formHome.append(form);
      modal.classList.remove('is-open');
      modal.hidden = true;
      document.body.classList.remove('quote-modal-open');
      previousFocus?.focus();
    };

    const open = (trigger) => {
      previousFocus = trigger;
      if (trigger.matches('[data-quote-link]')) {
        const quoteUrl = new URL(trigger.href, window.location.href);
        form.querySelectorAll('[data-quote-context]').forEach((field) => {
          field.value = quoteUrl.searchParams.get(field.dataset.quoteContext) || '';
        });
      }
      content.append(form);
      form.classList.add('expanded');
      modal.hidden = false;
      document.body.classList.add('quote-modal-open');
      requestAnimationFrame(() => modal.classList.add('is-open'));
      form.querySelector('input:not([type="hidden"]), select, textarea, button')?.focus();
    };

    document.querySelectorAll('[data-quote-modal-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        open(trigger);
      });
    });

    closeButton.addEventListener('click', close);
    modal.addEventListener('pointerdown', (event) => {
      if (event.target === modal) close();
    });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...modal.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    form.addEventListener('submit', () => {
      if (form.checkValidity()) close();
    });
  }

  bindGalleries() {
    document.querySelectorAll('[data-sunbow-gallery]').forEach((gallery) => {
      const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
      const counter = gallery.querySelector('[data-gallery-counter]');
      let current = 0;
      const loadSlide = (index) => {
        const image = slides[(index + slides.length) % slides.length]?.querySelector('img[data-gallery-src]');
        if (image && !image.hasAttribute('src')) image.src = image.dataset.gallerySrc;
      };
      const show = (index) => {
        current = (index + slides.length) % slides.length;
        loadSlide(current);
        loadSlide(current + 1);
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('active', slideIndex === current);
          slide.setAttribute('aria-hidden', String(slideIndex !== current));
        });
        gallery.querySelectorAll('[data-gallery-dot]').forEach((dot, dotIndex) => {
          dot.classList.toggle('active', dotIndex === current);
        });
        if (counter) counter.textContent = `${String(current + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
      };
      gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => show(current - 1));
      gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => show(current + 1));
      gallery.querySelectorAll('[data-gallery-dot]').forEach((dot, index) => dot.addEventListener('click', () => show(index)));
      show(0);
    });
  }

  bindStyleCards() {
    document.querySelectorAll('[data-style-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const details = document.getElementById(button.getAttribute('aria-controls'));
        if (!details) return;
        const opening = details.hidden;
        document.querySelectorAll('.option-details').forEach((panel) => { panel.hidden = true; });
        document.querySelectorAll('[data-style-toggle]').forEach((toggle) => {
          toggle.setAttribute('aria-expanded', 'false');
          toggle.querySelector('[data-toggle-label]').textContent = 'Explore this style';
        });
        details.hidden = !opening;
        button.setAttribute('aria-expanded', String(opening));
        button.querySelector('[data-toggle-label]').textContent = opening ? 'Close details' : 'Explore this style';
      });
    });
  }

  bindStyleGalleries() {
    document.querySelectorAll('[data-style-gallery]').forEach((gallery) => {
      const mainImage = gallery.querySelector('.option-detail-image img');
      const thumbnails = [...gallery.querySelectorAll('[data-style-gallery-thumb]')];
      if (!mainImage || !thumbnails.length) return;

      let current = Math.max(0, thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('active')));
      const show = (index) => {
        current = (index + thumbnails.length) % thumbnails.length;
        const selected = thumbnails[current];
        mainImage.removeAttribute('srcset');
        mainImage.removeAttribute('sizes');
        mainImage.src = selected.dataset.imageUrl;
        mainImage.alt = selected.dataset.imageAlt;
        thumbnails.forEach((item, itemIndex) => {
          const active = itemIndex === current;
          item.classList.toggle('active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        selected.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      };

      thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => show(index));
      });
      gallery.querySelector('[data-style-gallery-prev]')?.addEventListener('click', () => show(current - 1));
      gallery.querySelector('[data-style-gallery-next]')?.addEventListener('click', () => show(current + 1));
    });
  }

  captureAttribution() {
    window.SunbowAttribution?.populate?.();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new SunbowTheme());
} else {
  new SunbowTheme();
}
